import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Button, Badge } from 'react-bootstrap';
import { getArticleImageSlides } from '../utils/imageHelper';

const ArticleImageModal = ({ show, onHide, article, baseImageUrl }) => {
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    // Para cada slide, guardamos el índice de candidato que se está probando actualmente
    const [candidateIndices, setCandidateIndices] = useState({});

    // Obtenemos los slides de imágenes estructurados
    const slides = useMemo(() => {
        if (!article) return [];
        return getArticleImageSlides(article, baseImageUrl);
    }, [article, baseImageUrl]);

    // Resetear al abrir el modal o cambiar de artículo
    useEffect(() => {
        if (show) {
            setCurrentSlideIndex(0);
            setCandidateIndices({});
        }
    }, [show, article]);

    const totalSlides = slides.length;
    const currentSlide = slides[currentSlideIndex] || null;

    // Obtener la URL actual a mostrar para el slide activo
    const getSlideUrl = useCallback((slide) => {
        if (!slide || !slide.candidates || slide.candidates.length === 0) return null;
        const cIndex = candidateIndices[slide.index] || 0;
        return slide.candidates[cIndex] || null;
    }, [candidateIndices]);

    const currentUrl = getSlideUrl(currentSlide);
    const hasImage = Boolean(currentUrl);

    const handlePrev = useCallback(() => {
        if (totalSlides <= 1) return;
        setCurrentSlideIndex(prev => (prev > 0 ? prev - 1 : totalSlides - 1));
    }, [totalSlides]);

    const handleNext = useCallback(() => {
        if (totalSlides <= 1) return;
        setCurrentSlideIndex(prev => (prev < totalSlides - 1 ? prev + 1 : 0));
    }, [totalSlides]);

    // Navegación con flechas del teclado
    useEffect(() => {
        if (!show || totalSlides <= 1) return;
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                handlePrev();
            } else if (e.key === 'ArrowRight') {
                handleNext();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [show, totalSlides, handlePrev, handleNext]);

    const handleImageError = (slideIndex) => {
        setCandidateIndices(prev => {
            const currentCIndex = prev[slideIndex] || 0;
            const slide = slides.find(s => s.index === slideIndex);
            if (slide && currentCIndex < slide.candidates.length - 1) {
                return { ...prev, [slideIndex]: currentCIndex + 1 };
            }
            return prev;
        });
    };

    const handleResetAndClose = () => {
        setCurrentSlideIndex(0);
        setCandidateIndices({});
        onHide();
    };

    if (!article) return null;

    return (
        <Modal 
            show={show} 
            onHide={handleResetAndClose} 
            centered 
            size="lg"
            className="article-image-modal"
        >
            <Modal.Header closeButton className="bg-primary text-white py-2">
                <Modal.Title className="fs-6 d-flex align-items-center">
                    <i className="bi bi-images me-2"></i>
                    <span>Fotografía de Artículo</span>
                    {totalSlides > 1 && (
                        <Badge bg="light" text="dark" pill className="ms-2 small">
                            {totalSlides} fotos
                        </Badge>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-3 bg-light">
                {/* Cabecera con datos del producto */}
                <div className="bg-white p-3 rounded-3 shadow-sm border mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-2">
                            <Badge bg="dark" className="fs-6 px-2.5 py-1 font-monospace">
                                {article.id || article.c_artic || 'S/C'}
                            </Badge>
                            {article.rubroName && (
                                <Badge bg="info" className="text-dark fw-normal">
                                    {article.rubroName}
                                </Badge>
                            )}
                        </div>
                        {(article.price !== undefined && article.price !== null && article.price > 0) && (
                            <div className="fw-bold text-success fs-6">
                                ${new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2 }).format(article.price)}
                            </div>
                        )}
                    </div>
                    <h6 className="fw-bold text-primary mb-1 text-start">
                        {article.name || article.d_artic || 'Artículo sin descripción'}
                    </h6>
                    
                    {/* Descripción del artículo en caso de tenerla */}
                    {(article.d_arti2 || (article.description && article.description !== article.name && article.description !== article.d_artic) || article.d_tecni) && (
                        <div className="mt-2 text-start small bg-light p-2.5 rounded border">
                            <div className="d-flex align-items-center gap-1 text-primary fw-bold text-uppercase mb-1" style={{ fontSize: '0.72rem' }}>
                                <i className="bi bi-card-text"></i>
                                <span>Descripción:</span>
                            </div>
                            <div className="text-secondary lh-sm">
                                {article.d_arti2 && <p className="mb-1 fw-medium text-dark">{article.d_arti2}</p>}
                                {article.d_tecni && <p className="mb-1">{article.d_tecni}</p>}
                                {article.description && article.description !== article.name && article.description !== article.d_artic && article.description !== article.d_arti2 && (
                                    <p className="mb-1">{article.description}</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Contenedor Principal de la Imagen / Carrusel */}
                <div 
                    className="position-relative bg-white p-2 rounded-3 border shadow-sm d-flex justify-content-center align-items-center overflow-hidden" 
                    style={{ minHeight: '280px', maxHeight: '420px' }}
                >
                    {hasImage ? (
                        <>
                            <img 
                                key={`${currentSlideIndex}_${currentUrl}`}
                                src={currentUrl} 
                                alt={`${article.name || article.d_artic || 'Artículo'} - Foto ${currentSlideIndex + 1}`} 
                                className="img-fluid rounded-3"
                                style={{ maxHeight: '380px', width: 'auto', objectFit: 'contain', transition: 'all 0.25s ease' }}
                                onError={() => handleImageError(currentSlide.index)}
                            />

                            {/* Controles de Carrusel (si hay más de 1 imagen) */}
                            {totalSlides > 1 && (
                                <>
                                    {/* Botón Anterior */}
                                    <button
                                        type="button"
                                        className="btn btn-dark position-absolute top-50 start-0 translate-middle-y ms-2 rounded-circle d-flex align-items-center justify-content-center shadow"
                                        style={{ width: '40px', height: '40px', opacity: 0.85, zIndex: 10 }}
                                        onClick={handlePrev}
                                        title="Foto anterior (←)"
                                        aria-label="Foto anterior"
                                    >
                                        <i className="bi bi-chevron-left fs-5"></i>
                                    </button>

                                    {/* Botón Siguiente */}
                                    <button
                                        type="button"
                                        className="btn btn-dark position-absolute top-50 end-0 translate-middle-y me-2 rounded-circle d-flex align-items-center justify-content-center shadow"
                                        style={{ width: '40px', height: '40px', opacity: 0.85, zIndex: 10 }}
                                        onClick={handleNext}
                                        title="Foto siguiente (→)"
                                        aria-label="Foto siguiente"
                                    >
                                        <i className="bi bi-chevron-right fs-5"></i>
                                    </button>

                                    {/* Badge Contador de Fotos */}
                                    <div 
                                        className="position-absolute bottom-0 start-50 translate-middle-x mb-2 px-3 py-1 rounded-pill bg-dark bg-opacity-75 text-white small fw-bold shadow-sm"
                                        style={{ fontSize: '0.78rem', zIndex: 10, letterSpacing: '0.5px' }}
                                    >
                                        <i className="bi bi-camera me-1"></i>
                                        {currentSlideIndex + 1} / {totalSlides}
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div className="py-4 px-3 text-center">
                            <div className="mb-2">
                                <i className="bi bi-image text-muted opacity-50" style={{ fontSize: '4.5rem' }}></i>
                            </div>
                            <h6 className="text-secondary fw-bold mb-1">Sin Imagen Disponible</h6>
                            <small className="text-muted d-block">
                                Este artículo no tiene una fotografía cargada en el sistema.
                            </small>
                        </div>
                    )}
                </div>

                {/* Tira de Miniaturas (Thumbnails) para navegación rápida */}
                {totalSlides > 1 && (
                    <div className="d-flex align-items-center justify-content-center gap-2 mt-3 p-2 bg-white rounded-3 border shadow-sm overflow-auto">
                        {slides.map((slide, idx) => {
                            const thumbUrl = getSlideUrl(slide);
                            const isActive = idx === currentSlideIndex;
                            return (
                                <button
                                    key={slide.index}
                                    type="button"
                                    className={`btn p-1 rounded-3 border transition-all ${
                                        isActive ? 'border-primary border-2 shadow-sm' : 'border-light-subtle opacity-75'
                                    }`}
                                    style={{
                                        width: '64px',
                                        height: '64px',
                                        background: '#f8fafc',
                                        cursor: 'pointer',
                                        transform: isActive ? 'scale(1.05)' : 'scale(1)'
                                    }}
                                    onClick={() => setCurrentSlideIndex(idx)}
                                    title={`Ver foto ${idx + 1}`}
                                >
                                    <img
                                        src={thumbUrl}
                                        alt={`Miniatura ${idx + 1}`}
                                        className="w-100 h-100 rounded"
                                        style={{ objectFit: 'cover' }}
                                        onError={() => handleImageError(slide.index)}
                                    />
                                </button>
                            );
                        })}
                    </div>
                )}

                {article.notes && (
                    <div className="mt-3 text-start small text-muted bg-white p-2 rounded-3 border">
                        <strong>Notas:</strong> {article.notes}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="py-2 bg-light d-flex justify-content-between align-items-center">
                <div className="small text-muted">
                    {totalSlides > 1 && (
                        <span><i className="bi bi-keyboard me-1"></i>Podés usar las flechas del teclado ← → para navegar</span>
                    )}
                </div>
                <Button variant="secondary" size="sm" onClick={handleResetAndClose} className="px-4 fw-bold rounded-pill">
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ArticleImageModal;

